import { useNavigate } from "react-router";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import { Loading } from "@medoma/components/Loading/Loading";
import { Text } from "@medoma/components/Text/Text";
import {
  patientStatusSchema,
  type IPatient,
  type IRegisteredPatient,
} from "@medoma/models/patients";
import Form from "@/components/Form/Form";
import { declinePatient, useParentRoutesPatient } from "@/api/Patients";
import { useActivitiesToCloseForPatient } from "@/api/Activities";
import { deducedError } from "@/Utils/ErrorUtils";
import {
  ActivitiesToClose,
  type IActivitiesToCloseFields,
} from "../ActivitiesToClose/ActivitiesToClose";
import styles from "./DeclinePatient.module.scss";

const DeclinePatientForm = ({
  patient,
  continueTo,
}: {
  patient: IRegisteredPatient;
  continueTo: string;
}) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: activities = [],
    isPending: isFetchingActivities,
    isError: couldNotFetchActivities,
  } = useActivitiesToCloseForPatient(patient.id);

  const methods = useForm<IActivitiesToCloseFields>({
    // Recurring activities are preselected once they have been fetched;
    // one-time activities are left unchecked so the user opts in explicitly
    values: {
      activityIds: activities
        .filter(({ recurring }) => recurring)
        .map(({ id }) => id),
      activitiesAreClosed: false,
    },
    resetOptions: { keepDirtyValues: true },
  });
  const {
    formState: { errors },
    handleSubmit,
    setError,
  } = methods;

  const { mutate: declinePatientMutation, isPending: isDeclining } =
    useMutation({
      mutationFn: (activitiesToClose: string[]) =>
        declinePatient({ patientId: patient.id, activitiesToClose }),
      onSuccess: async () => {
        // Pages that aren't shown may have cached the declined patient or a
        // removed visit, so they are fetched anew instead of shown as before
        queryClient.removeQueries({ type: "inactive" });
        await navigate(continueTo, { replace: true });
        await queryClient.invalidateQueries();
      },
      onError: (error) => {
        setError("root.server", {
          message: `${t`Det gick inte att avslå patienten.`} ${deducedError(error)}`,
        });
      },
    });

  const validateAndDecline = handleSubmit(({ activityIds }) =>
    declinePatientMutation(activityIds),
  );

  if (isFetchingActivities) {
    return <Loading message={t`Hämtar information om aktiviteter`} />;
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndDecline}>
        <Text element="p">
          <Trans>
            OBS! Säkerställ först att det inte finns några olästa chattnotiser
            från patienten.
          </Trans>
        </Text>
        <ActivitiesToClose
          activities={activities}
          couldNotFetchActivities={couldNotFetchActivities}
          instructions={t`Välj de aktiviteter som ska avslutas vid avslag`}
        />
        {errors.root?.server?.message ? (
          <ErrorMessage message={errors.root.server.message} />
        ) : null}
        <FilledButton disabled={isDeclining} type="submit">
          <Trans>Avslå</Trans>
        </FilledButton>
      </Form>
    </FormProvider>
  );
};

export const DeclinePatient = ({
  patient,
  continueTo,
}: {
  patient: IPatient;
  // Where to continue once the patient is declined, as the declined patient and
  // a visit whose activities were closed can no longer be worked with
  continueTo: string;
}) => {
  const { t } = useLingui();

  return (
    <section className={styles.declinePatient}>
      <Heading level="h2">
        <Trans>Avslå</Trans>
      </Heading>
      {patient.status === patientStatusSchema.enum.prospect ? (
        <DeclinePatientForm patient={patient} continueTo={continueTo} />
      ) : (
        <ErrorMessage
          message={t`Patienten är inte under bedömning och kan därför inte avslås.`}
        />
      )}
    </section>
  );
};

export const DeclineParentRoutesPatient = () => (
  <DeclinePatient patient={useParentRoutesPatient()} continueTo="/patients" />
);
